import { SxProps, TextFieldProps, Theme, Typography } from "@mui/material";
import { ReactElement } from "react";
import {
  FieldValues,
  Path,
  useController,
  UseControllerProps,
} from "react-hook-form";

import { FlexBox } from "~frontend/components/FlexBox";

import { RhfCurrencyField } from "./RhfCurrencyField";
import { RhfNumberField } from "./RhfNumberField";

export interface RhfNumericalRangeProps<T extends FieldValues> {
  control: UseControllerProps<T>["control"];
  name: UseControllerProps<T>["name"];
  fullWidth?: boolean;
  bottomLabel?: string;
  lowLabel?: string;
  highLabel?: string;
  fieldKey?: string;
  variant?: TextFieldProps["variant"];
  inputSx?: TextFieldProps["sx"];
  slotProps?: TextFieldProps["slotProps"];
  styleVariant?: "number" | "currency";
  adornmentOffset?: number;
  bottomLabelSx?: SxProps<Theme>;
}

export function RhfNumericalRange<T extends FieldValues>({
  control,
  name,
  fullWidth = true,
  bottomLabel,
  lowLabel = "Lower Limit",
  highLabel = "Upper Limit",
  fieldKey,
  variant,
  inputSx,
  slotProps,
  styleVariant = "number",
  adornmentOffset,
  bottomLabelSx,
}: RhfNumericalRangeProps<T>): ReactElement {
  const { fieldState } = useController({ control, name });
  const errorMessage = fieldState.error ? fieldState.error.message : undefined;

  const Component =
    styleVariant === "currency" ? RhfCurrencyField : RhfNumberField;

  return (
    <FlexBox direction="column" sx={{ width: fullWidth ? "100%" : "auto" }}>
      <FlexBox gap={2}>
        <Component
          key={fieldKey ? `${fieldKey}-low` : undefined}
          control={control}
          name={`${name}[0]` as Path<T>}
          label={lowLabel}
          fullWidth
          variant={variant}
          sx={inputSx}
          slotProps={slotProps}
          adornmentOffset={adornmentOffset}
        />
        <Component
          key={fieldKey ? `${fieldKey}-high` : undefined}
          control={control}
          name={`${name}[1]` as Path<T>}
          label={highLabel}
          fullWidth
          variant={variant}
          sx={inputSx}
          slotProps={slotProps}
          adornmentOffset={adornmentOffset}
        />
      </FlexBox>
      {(bottomLabel || errorMessage) && (
        <Typography
          variant="caption"
          color={errorMessage ? "error" : "textSecondary"}
          sx={{ mt: -1, ...(bottomLabelSx || {}) }}
        >
          {errorMessage || bottomLabel}
        </Typography>
      )}
    </FlexBox>
  );
}
